import React, {createRef, useEffect, useState} from 'react';
import ProductsManager from "../managers/productsManager/productsManager";
import {useNavigate, useParams} from "react-router";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid2";
import {FormGrid} from "../checkout/components/billingData/DataForm";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import {CardContent, FormGroup, Paper} from "@mui/material";
import {FileUploadOutlined} from "@mui/icons-material";
import FunctionHandler from "../utils/functionHandler";
import OrderDesigns from "../user/userPage/components/OrderDesigns";
import {Trans} from "react-i18next";
import ProductContract from "../managers/productsManager/contracts/productContract";
import ParamTypes from "../productParamHandler/paramTypes";
import Typography from "@mui/material/Typography";
import ParamContract from "../productParamHandler/components/contracts/ParamContract";
import {doc, getFirestore, setDoc} from "firebase/firestore";
import app from "../firebaseConfig/firebaseConfig";
import ArrayShifter from "./ArrayShifter";
import {getDatabase, onValue, ref} from "firebase/database";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

const textValues = ['name', 'description'];
const numberValues = ['necessaryDesigns', 'price', 'minPiece'];
const db = getDatabase(app);
const categoriesRef = ref(db, 'categories');

const fileTypes = ["JPG", "JPEG", "PNG", "SVG+XML", "WEBP"];
const fireStoreDB = getFirestore(app);

function ProductEditor({db}) {
    const navigate = useNavigate();
    const [product, setProduct] = useState();
    const params = useParams();

    const [newParamKey, setNewParamKey] = useState();
    const [newProduct, setNewProduct] = useState();
    const inputRef = createRef();
    const designInputRef = createRef();
    const handleClick = React.useCallback(() => inputRef.current?.click(), [inputRef]);
    const handleDesignClick = React.useCallback(() => designInputRef.current?.click(), [designInputRef]);
    const [categories, setCategories] = useState({});

    useEffect(() => {
        onValue(categoriesRef, snapshot => {
            setCategories(snapshot.val() || {});
        })
    }, []);


    const fetchData = async (id) => {
        const data = await ProductsManager.getProduct(id);
        setProduct(data);
    };

    useEffect(() => {
        if (!params.id.startsWith('clone-product-') && params.id !== 'new-product') {
            fetchData(params.id);
        }
    }, [params.id]);

    useEffect(() => {
        if (!product) {
            return;
        }

        setNewProduct({...product});
    }, [product])

    useEffect(() => {
        if (params.id.startsWith('clone-product-')) {
            fetchData(params.id.replace('clone-product-', ''));
        }

        if (params.id === 'new-product') {
            setNewProduct(new ProductContract());
        }

    }, [params.id])

    const onChange = (id, value) => {
        newProduct[id] = value;
        setNewProduct({...newProduct});
    }

    const submit = () => {
        const id = params.id;
        if (id === 'new-product' || id.startsWith('clone-product-')) {
            if (newProduct.id) {
                setDoc(doc(fireStoreDB, "products", newProduct.id), JSON.parse(JSON.stringify(newProduct)));
            }
        } else {
            setDoc(doc(fireStoreDB, "products", params.id), JSON.parse(JSON.stringify(newProduct)));
        }

        navigate('/admin/products');
    }

    const paramChange = (param, index) => {
        newProduct.params = newProduct.params || [];

        if (typeof index === 'number') {
            newProduct.params[index] = param;
        } else {
            newProduct.params.push(param);
        }

        setNewProduct({...newProduct});
    }

    const onFileUpload = (event) => {
        const formData = new FormData();
        Array.from(event.target.files).forEach(file => {
                formData.append('file', file);
            }
        );

        formData.append('productId', newProduct.id);

        FunctionHandler.sendFile('/upload-product-images', formData, (data) => {
            const newImages = (data?.uploadedFiles || []).map(src => ({
                src: FunctionHandler.getFunctionUrl(`/dep/${encodeURIComponent(src)}`),
                thumbnail: FunctionHandler.getFunctionUrl(`/dep/${encodeURIComponent(`thumbnail/${src.replace(/.svg$/, '.png')}`)}`),
                alt: newProduct.name
            }));
            newProduct.images = (newProduct.images || []).concat(newImages);
            setNewProduct({...newProduct});
        });

        if (inputRef.current) {
            inputRef.current.value = "";
            inputRef.current.type = "text";
            inputRef.current.type = "file";
        }
    }

    const onDesignUpload = (event) => {
        const formData = new FormData();
        Array.from(event.target.files).forEach(file => {
                formData.append('file', file);
            }
        );

        formData.append('printDesign', newProduct.id);

        FunctionHandler.sendFile('/upload', formData, (data) => {
            const newImages = (data?.uploadedFiles || []).map(src => ({
                src: FunctionHandler.getFunctionUrl(`/dep/${encodeURIComponent(src)}`),
                thumbnail: FunctionHandler.getFunctionUrl(`/dep/${encodeURIComponent(`thumbnail/${src.replace(/.svg$/, '.png')}`)}`),
                alt: newProduct.name
            }));
            newProduct.designs = (newProduct.designs || []).concat(newImages);
            setNewProduct({...newProduct});
        });

        if (designInputRef.current) {
            designInputRef.current.value = "";
            designInputRef.current.type = "text";
            designInputRef.current.type = "file";
        }
    }

    const removeImage = (index) => {
        newProduct.images.splice(index, 1);
        setNewProduct({...newProduct});
    }

    const removeDesign = (index) => {
        newProduct.designs.splice(index, 1);
        setNewProduct({...newProduct});
    }

    const addColors = () => {
        newProduct.params = newProduct.params || [];
        newProduct.params.push(new ParamContract(ParamTypes.COLORS, newParamKey));

        setNewParamKey('');
        setNewProduct({...newProduct});
    }

    const addDropDown = () => {
        newProduct.params = newProduct.params || [];
        newProduct.params.push(new ParamContract(ParamTypes.DROP_DOWN, newParamKey));

        setNewParamKey('');
        setNewProduct({...newProduct});
    }

    const addText = () => {
        newProduct.params = newProduct.params || [];
        newProduct.params.push(new ParamContract(ParamTypes.TEXT, newParamKey));

        setNewParamKey('');
        setNewProduct({...newProduct});
    }

    const addSlider = () => {
        newProduct.params = newProduct.params || [];
        newProduct.params.push(new ParamContract(ParamTypes.SIZE, 'size', {}));

        setNewParamKey('');
        setNewProduct({...newProduct});
    }

    const addFileUpload = () => {
        newProduct.params = newProduct.params || [];
        newProduct.params.push(new ParamContract(ParamTypes.FILE, 'file', [{width: 15, ratio: 0, path: ''}]));

        setNewParamKey('');
        setNewProduct({...newProduct});
    }

    const deleteParam = (index) => {
        newProduct.params = newProduct.params || [];
        newProduct.params.splice(index, 1);
        setNewProduct({...newProduct});
    }

    const orderImagesOrder = (oldIndex, newIndex) => {
        const item = newProduct.images.splice(oldIndex, 1)[0];
        newProduct.images.splice(newIndex, 0, item);
        setNewProduct({...newProduct});
    }

    const orderParams = (oldIndex, newIndex) => {
        const item = newProduct.params.splice(oldIndex, 1)[0];
        newProduct.params.splice(newIndex, 0, item);
        setNewProduct({...newProduct});
    }

    const categoryChange = (event, id) => {
        newProduct.categories = newProduct.categories || [];
        if (event.target.checked) {
            newProduct.categories.push(id);
        } else {
            newProduct.categories = newProduct.categories.filter(category => category !== id);
        }

        setNewProduct({...newProduct});
    }

    return (newProduct &&
        <Grid container spacing={2}>
            <Button sx={{mt: 2}} color={'secondary'} onClick={() => navigate('/admin/products')}>&lt; <Trans i18nKey={'buttons.back'}/></Button>
            <Button sx={{mt: 2}} color={'secondary'}
                    onClick={() => navigate(`/products/${(params.id === 'new-product' || params.id.startsWith('clone-product-')) ? newProduct.id : params.id}`)}>To
                product</Button>
            {(params.id === 'new-product' || params.id.startsWith('clone-product-')) ?
                <FormGrid key={'id'} size={{xs: 12}}>
                    <FormLabel htmlFor="name">
                        Id
                    </FormLabel>
                    <TextField
                        id='id'
                        type="text"
                        size="small"
                        value={newProduct.id}
                        onChange={(event) => onChange('id', event.target.value)}
                    />
                </FormGrid> : ''}

            {textValues.map(key => <FormGrid key={key} size={{xs: 12}}>
                <FormLabel htmlFor="name">
                    {key}
                </FormLabel>
                <TextField
                    id={key}
                    type="text"
                    size="small"
                    value={newProduct[key]}
                    onChange={(event) => onChange(key, event.target.value)}
                />
            </FormGrid>)}

            {numberValues.map(key => <FormGrid key={key} size={{xs: 12}}>
                <FormLabel htmlFor="name">
                    {key}
                </FormLabel>
                <TextField
                    id={key}
                    type="number"
                    size="small"
                    value={newProduct[key]}
                    onChange={(event) => onChange(key, event.target.value)}
                />
            </FormGrid>)}
            <Typography>Product pictures</Typography>
            <Grid container gap={2} size={12}>
                <Grid sx={{
                    border: '2px solid',
                    borderColor: 'transparent',
                    pt: 2
                }}>
                    <CardContent sx={{
                        width: 64,
                        height: 64,
                        cursor: 'pointer',
                        border: '2px dashed',
                        borderColor: 'primary.main'
                    }} onClick={handleClick}>
                        <FileUploadOutlined sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)'
                        }} color="secondary"/>
                        <input type="file" style={{display: 'none'}} ref={inputRef}
                               onChange={onFileUpload}
                               accept={fileTypes.map(ext => `image/${ext}`).join(', ')}
                               multiple={true}/>
                    </CardContent>
                </Grid>
                <OrderDesigns images={newProduct.images || []} withDeleteButton removeImage={removeImage} pt={0}
                              orderable orderItem={orderImagesOrder}/>
            </Grid>
            <Typography>Designs</Typography>

            <Grid container gap={2} size={12}>
                <Grid sx={{
                    border: '2px solid',
                    borderColor: 'transparent',
                    pt: 2
                }}>
                    <CardContent sx={{
                        width: 64,
                        height: 64,
                        cursor: 'pointer',
                        border: '2px dashed',
                        borderColor: 'primary.main'
                    }} onClick={handleDesignClick}>
                        <FileUploadOutlined sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)'
                        }} color="secondary"/>
                        <input type="file" style={{display: 'none'}} ref={designInputRef}
                               onChange={onDesignUpload}
                               accept={fileTypes.map(ext => `image/${ext}`).join(', ')}
                               multiple={true}/>
                    </CardContent>
                </Grid>
                <OrderDesigns images={newProduct.designs || []} withDeleteButton removeImage={removeDesign} pt={0} withLink/>
            </Grid>

            <Paper sx={{width: '100%', p: 3}}>
                <Typography fontWeight={500} sx={{pb: 2}}>Categories:</Typography>
                <FormGroup>
                    {Object.keys(categories).map(categoryId => <FormControlLabel
                        key={categoryId}
                        control={<Checkbox onChange={(event) => categoryChange(event, categoryId)}
                                           checked={newProduct?.categories?.indexOf(categoryId) > -1}/>}
                        label={categoryId}/>
                    )}
                </FormGroup>
            </Paper>

            <Paper sx={{width: '100%', p: 3}}>
                <Typography fontWeight={500} sx={{pb: 2}}>Parameters:</Typography>
                <CardContent>
                    <Typography sx={{pb: 2}}>Add param:</Typography>
                    <TextField value={newParamKey} onChange={(event) => setNewParamKey(event.target.value)}
                               size={'small'}></TextField>
                    <Button onClick={addColors}>Add colors</Button>
                    <Button onClick={addDropDown}>Add drop down</Button>
                    <Button onClick={addText}>Add text</Button>
                    <Button onClick={addSlider}>Add size</Button>
                    <Button onClick={addFileUpload}>Add file upload</Button>
                </CardContent>

                <Grid container>
                    {(newProduct.params || []).map((param, index) => <Grid key={index} size={12}>
                        <CardContent>
                            {ParamTypes.GET_ADMIN_COMPONENT(param, {
                                onChange: (param) => paramChange(param, index),
                                onDelete: () => deleteParam(index)
                            })}
                            <ArrayShifter horizontal index={index} shiftTo={orderParams}
                                          length={(newProduct.params || []).length}/>
                        </CardContent>
                    </Grid>)}
                </Grid>
            </Paper>

            <Box sx={{display: 'flex', justifyContent: 'flex-end', width: '100%'}}>
                <Button onClick={submit} variant={'contained'}>Submit</Button>
            </Box>
        </Grid>
    );
}

export default ProductEditor;
